import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EmergencyDataView from '../components/EmergencyDataView';
import { validateEmergencyToken, validateEmergencyPin } from '../services/emergencyService';
import type { EmergencyHealthData } from '../types/emergency';
import './EmergencyAccess.css';

const EmergencyAccess: React.FC = () => {
  const { token } = useParams<{ token?: string }>();

  const [loading, setLoading] = useState(false);
  const [healthData, setHealthData] = useState<EmergencyHealthData | null>(null);
  const [error, setError] = useState('');
  const [used, setUsed] = useState(false);
  const [failCount, setFailCount] = useState(0);

  // PIN form state
  const [accessId, setAccessId] = useState('');
  const [pin, setPin] = useState('');

  // If token is in URL, validate it immediately
  useEffect(() => {
    if (token) {
      handleTokenAccess(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleTokenAccess = async (t: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await validateEmergencyToken(t);
      if (result.success && result.data) {
        setHealthData(result.data);
      } else if (result.error?.includes('already used') || result.error?.includes('used')) {
        setUsed(true);
      } else {
        setError(result.error || 'Invalid or expired emergency code.');
      }
    } catch (err: any) {
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessId.trim() || !pin.trim()) return;

    setLoading(true);
    setError('');
    try {
      const result = await validateEmergencyPin(accessId.trim().toUpperCase(), pin.trim());
      if (result.success && result.data) {
        setHealthData(result.data);
      } else if (result.error?.includes('already used') || result.error?.includes('used')) {
        setUsed(true);
      } else if (result.error?.includes('rate limit') || result.error?.includes('too many')) {
        setFailCount(5);
        setError('Too many failed attempts. Please wait 15 minutes before trying again.');
      } else {
        setFailCount(prev => prev + 1);
        setError(result.error || 'Invalid Access ID or PIN.');
      }
    } catch (err: any) {
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="emergency-page">
        <div className="emergency-page-header">
          <img src="/Logo Trimmed.png" alt="SilverScribe" className="emergency-logo" />
          <h1>Emergency Access</h1>
        </div>
        <div className="emergency-loading">
          <div className="spinner" />
          <p>Verifying emergency access code...</p>
        </div>
      </div>
    );
  }

  // Already used
  if (used) {
    return (
      <div className="emergency-page">
        <div className="emergency-page-header">
          <img src="/Logo Trimmed.png" alt="SilverScribe" className="emergency-logo" />
          <h1>Emergency Access</h1>
        </div>
        <div className="emergency-used">
          <h2>Code Already Used</h2>
          <p>This emergency access code has already been used. For security, each code is single-use only.</p>
          <p>The patient must generate a new code from their SilverScribe account.</p>
        </div>
      </div>
    );
  }

  // Health data loaded — show it
  if (healthData) {
    return (
      <div className="emergency-page">
        <div className="emergency-page-header">
          <img src="/Logo Trimmed.png" alt="SilverScribe" className="emergency-logo" />
          <h1>Emergency Access</h1>
        </div>
        <div className="emergency-data-container">
          <EmergencyDataView data={healthData} />
        </div>
      </div>
    );
  }

  // PIN entry form (no token in URL, or error fallback)
  return (
    <div className="emergency-page">
      <div className="emergency-page-logo">
        <img src="/Logo Trimmed.png" alt="SilverScribe" className="emergency-logo" />
      </div>
      <div className="emergency-page-header">
        <h1>Emergency Access</h1>
        <p>Enter a patient's emergency access credentials</p>
      </div>

      {error && (
        <div className="emergency-error">
          <p>{error}</p>
          {failCount > 0 && failCount < 5 && (
            <p className="error-detail">
              {5 - failCount} attempt{5 - failCount !== 1 ? 's' : ''} remaining before lockout
            </p>
          )}
        </div>
      )}

      {failCount >= 5 ? (
        <div className="rate-limit-warning">
          <p>Access temporarily locked. Please wait 15 minutes.</p>
        </div>
      ) : (
        <form className="emergency-pin-form" onSubmit={handlePinSubmit}>
          <h2>Patient Emergency Code</h2>
          <p className="form-subtitle">Found on the patient's wallet card or bracelet</p>

          <div className="form-group">
            <label htmlFor="accessId">Access ID</label>
            <input
              id="accessId"
              type="text"
              value={accessId}
              onChange={(e) => setAccessId(e.target.value.toUpperCase().slice(0, 8))}
              placeholder="e.g. AB12CD34"
              maxLength={8}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="pin">PIN</label>
            <input
              id="pin"
              type="text"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit PIN"
              maxLength={6}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            className="emergency-submit-btn"
            disabled={loading || accessId.length < 8 || pin.length < 6}
          >
            Access Health Information
          </button>
        </form>
      )}
    </div>
  );
};

export default EmergencyAccess;
